// Small helper that wraps the backend API and falls back to localStorage
// if the network call fails (so we never lose a lead).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

/**
 * Submit a lead to the backend.
 * @param {object} lead - full payload (must include `source`)
 * @returns {Promise<{ok:boolean, lead?:object, error?:string}>}
 */
export async function submitLead(lead) {
  try {
    const resp = await fetch(`${API}/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(lead),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      // Save to localStorage anyway so it isn't lost
      _saveLocal(lead);
      return { ok: false, error: err.detail || `HTTP ${resp.status}` };
    }
    const data = await resp.json();
    // Also keep a local copy for the admin fallback / offline view
    _saveLocal({ ...lead, ts: data.created_at });
    return { ok: true, lead: data };
  } catch (e) {
    _saveLocal(lead);
    return { ok: false, error: "Network error — saved locally" };
  }
}

function _saveLocal(lead) {
  try {
    const key = "st_leads_all";
    const existing = JSON.parse(localStorage.getItem(key) || "[]");
    existing.push({ ...lead, ts: lead.ts || new Date().toISOString() });
    localStorage.setItem(key, JSON.stringify(existing));
  } catch (e) {
    // ignore
  }
}

// ------------------ Admin helpers ------------------
const TOKEN_KEY = "st_admin_jwt";

export function setAdminToken(token) {
  // Use localStorage so the 7-day JWT survives tab close.
  localStorage.setItem(TOKEN_KEY, token);
}

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAdminToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function adminLogin(password) {
  const resp = await fetch(`${API}/admin/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!resp.ok) throw new Error("Wrong password");
  const data = await resp.json();
  setAdminToken(data.token);
  return data;
}

function authHeaders(extra = {}) {
  const token = getAdminToken();
  if (!token) throw new Error("Not authenticated");
  return { Authorization: `Bearer ${token}`, ...extra };
}

export async function fetchLeads() {
  const resp = await fetch(`${API}/admin/leads`, {
    headers: authHeaders(),
  });
  if (resp.status === 401) {
    clearAdminToken();
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

export async function updateLeadStatus(leadId, status) {
  const resp = await fetch(`${API}/admin/leads/${leadId}`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ status }),
  });
  if (resp.status === 401) {
    clearAdminToken();
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw new Error("Update failed");
  return resp.json();
}

export async function deleteLead(leadId) {
  const resp = await fetch(`${API}/admin/leads/${leadId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (resp.status === 401) {
    clearAdminToken();
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw new Error("Delete failed");
  return resp.json();
}

export async function clearAllLeadsApi() {
  const resp = await fetch(`${API}/admin/leads`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (resp.status === 401) {
    clearAdminToken();
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw new Error("Clear failed");
  return resp.json();
}
